import {IServiceFactory} from "../../../service-factory.interface";
import {IRealityCheckLastOccurrence} from "./reality-check-last-occurence.interface";
import {LocalStorageKeys} from "../../../storage/local-storage-keys";
import {TabLockKey} from "../../../storage/local-storage-service.interface";
import {IStorageLock} from "../../../storage/storage-lock.interface";

export class RealityCheckLastOccurrenceStorage {
    constructor(private readonly services: IServiceFactory) {

    }

    dispose(): void {
        this.services.localStorage.removeItem(LocalStorageKeys.realityCheckLastOccurrence);
    }

    private async _decryptStorageData(): Promise<IRealityCheckLastOccurrence | null> {
        try {
            let encryptedData = this.services.localStorage.getItem(LocalStorageKeys.realityCheckLastOccurrence);
            return await this.services.crypto.decryptJson<IRealityCheckLastOccurrence>(encryptedData);
        } catch (err) {
            this.services.logger.error(`Failed to decrypt stored last RC occurrence value`);
            return null;
        }

    }

    private async _getStorageData(): Promise<IRealityCheckLastOccurrence> {
        let data = await this._decryptStorageData();

        if(!data) {
            data = {
                index: 0,
                isConfirmed: false,
                sentEvents: {},
                businessCorrelationId: this.services.tools.getGuid()
            }
            const encryptedData = await this.services.crypto.encryptJson(data);
            this.services.localStorage.setItem(LocalStorageKeys.realityCheckLastOccurrence, encryptedData);
        }

        return data;
    }

    private async _setStorageData(payload: Partial<IRealityCheckLastOccurrence>): Promise<void> {
        payload = {
            ...(await this._getStorageData()),
            ...payload
        };
        const encryptedPayload = await this.services.crypto.encryptJson(payload);

        this.services.localStorage.setItem(LocalStorageKeys.realityCheckLastOccurrence, encryptedPayload);
    }

    async getLastOccurrenceIndex(): Promise<number> {
        return (await this._getStorageData()).index;
    }

    async setNewOccurrenceIndex(index: number): Promise<boolean> {
        const lock = await this._acquireLock();
        if(!lock.isAcquired) {
            return false;
        }

        try {
            const storageData = await this._getStorageData();
            if(storageData.index >= index) {
                return false;
            }

            const freshStorageData: IRealityCheckLastOccurrence = {
                index: index,
                isConfirmed: false,
                businessCorrelationId: this.services.tools.getGuid(),
                sentEvents: {}
            };

            await this._setStorageData(freshStorageData);
            return true;

        } finally {
            lock.release();
        }

    }



    async sendAnalyticsEvent(eventKey: string, sendEvent: (businessCorrelationId: string) => Promise<void>): Promise<void> {
        const lock = await this._acquireLock();

        if(!lock.isAcquired) {
            return;
        }

        let businessCorrelationId: string;

        try {
            const currentStorageData = await this._getStorageData();
            if(currentStorageData.sentEvents[eventKey]) {
                //it means the event was already sent from another tab
                return;
            }

            businessCorrelationId = currentStorageData.businessCorrelationId;

            await this._setStorageData({
                ...currentStorageData,
                sentEvents: {
                    ...currentStorageData.sentEvents,
                    [eventKey]: true
                }
            })
        } finally {
            lock.release();
        }

        await sendEvent(businessCorrelationId);
    }

    async getBusinessCorrelationId(): Promise<string> {
        return (await this._getStorageData()).businessCorrelationId;
    }

    private async _acquireLock(): Promise<IStorageLock> {
        return await this.services.localStorage.acquireTabLock({
            lockKey: new TabLockKey(LocalStorageKeys.realityCheckLastOccurrence),
            browserTabId: this.services.application.currentBrowserTabId
        })
    }

    async isConfirmedByUser(): Promise<boolean> {
        return (await this._getStorageData()).isConfirmed;
    }


    async confirm(): Promise<void> {
        const lock = await this._acquireLock();

        if(!lock.isAcquired) {
            return;
        }

        try {
            await this._setStorageData({
                isConfirmed: true
            });
        } finally {
            lock.release();
        }
    }
}